import unionBy from 'lodash/unionBy';
import { IActTeamMemberAvailableList, UserStatus } from '~/services/api/actTeamMember/types';
import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
  IRelatedClientsForAllocation,
} from '~/services/api/clientAllocation/types';
import { IRole } from '~/store/user/types';
import { IOption } from '~/types';

export interface ITeamMemberMapped {
  id: number;
  fullName: string;
  photo: string | null;
  isDeleted: boolean;
  roles?: IRole[];
}

export function mergeAllocationByUserId(
  clientAllocations: IClientAllocationDetailed[],
): IClientAllocationDetailed[] {
  const mergedArray: IClientAllocationDetailed[] = [];

  clientAllocations.forEach(item => {
    // Find an existing entry with the same user ID
    const existing = mergedArray.find(i => i.user.id === item.user.id && !i.isArchived);

    if (existing) {
      if (item.isCrisis) {
        existing.isCrisis = true;
        existing.crisisClients = unionBy(
          [
            ...(existing.crisisClients || []).map(client => ({ ...client })),
            ...(item.user.clients || []).map(client => ({
              ...client,
              resolved: client.resolved ?? item.resolved,
            })),
          ],
          'id',
        );
      } else {
        existing.user = {
          ...existing.user,
          clients: unionBy(
            [...(existing.user.clients || []), ...(item.user.clients || [])].map(client => ({
              ...client,
            })),
            'id',
          ),
        };
      }

      // Merge locationGroups, ensuring immutability
      existing.locationGroups = unionBy(
        [...(existing.locationGroups || []), ...(item.locationGroups || [])].map(group => ({
          ...group,
        })),
        'id',
      );
    } else {
      const clients = item.isCrisis ? [] : item.user.clients.map(client => ({ ...client }));
      const crisisClients = item.isCrisis
        ? item.user.clients.map(client => ({
            ...client,
            resolved: client.resolved ?? item.resolved,
          }))
        : [];

      mergedArray.push({
        ...item,
        user: {
          ...item.user,
          clients,
        },
        locationGroups: item.locationGroups.map(group => ({ ...group })),
        crisisClients,
      });
    }
  });

  return mergedArray;
}

export const teamMemberMapper = (teamMember: IActTeamMemberAvailableList): ITeamMemberMapped => ({
  id: teamMember.id,
  fullName: `${teamMember.firstName} ${teamMember.lastName}`,
  photo: teamMember.photo,
  isDeleted:
    teamMember.isDeleted || !teamMember.isActive || teamMember.status === UserStatus.PENDING,
  roles: teamMember.roles,
});

export const clientMapper = (client: IRelatedClientsForAllocation): IOption => ({
  value: client.id,
  label: `${client.firstName} ${client.lastName}`,
  photo: client.photo,
  name: `${client.firstName} ${client.lastName}`,
  id: client.id,
  teams: [client.actTeam],
  memo: '',
});

export const formatLocationGroupOptions = (groups: IAllocationClientsByGroup[]): IOption[] =>
  (groups || []).map(group => ({
    value: group.locationGroup.id,
    label: group.locationGroup.name,
  }));

export const sortByName = (
  a: IActTeamMemberAvailableList,
  b: IActTeamMemberAvailableList,
): number => (a.lastName.toLocaleLowerCase() < b.lastName.toLocaleLowerCase() ? -1 : 1);
